import { Injectable } from '@angular/core'
import { reactor } from '../reactor'
import { customerOperations } from '../stores/customer-store'
import { PostsService, PostLoadContext } from '../services/posts-service'
import { WritersService } from '../services/writers-service'
import { RatingsService } from '../services/ratings-service'
import { KeywordsService } from '../services/keywords-service'
import { CustomersService } from '../services/customers-service'
import { PostEditRequestsService } from '../services/post-edit-requests-service'
import { infoToast, errorToast, successToast } from '../toaster'
import { donePromise } from '../utils'
import { Post, Writer, Rating, Keyword, PostReview, PostEditRequest, Customer, PostExtraCode } from '../types'
import groupBy from 'lodash-es/groupBy'
import { get } from 'lodash-es'
import { PurchasePostFormPaymentOptionType } from 'src/interfaces/post-credit-payment.interface'
import { Router } from '@angular/router'
import { sweetAlert } from 'bmAlert'

type PostsData = {
  posts: Post[],
  totalPosts?: number,
  keywords: Keyword[],
  writers: Writer[],
  ratings: Rating[]
};

type PostData = {
  post: Post,
  keyword: Keyword,
  writer: Writer,
  rating: Rating
};


@Injectable({providedIn: 'root'})
export class PostActions {
  postsService = new PostsService();
  writersService = new WritersService();
  ratingsService = new RatingsService();
  keywordsService = new KeywordsService();
  customersService = new CustomersService();
  postEditRequestsService = new PostEditRequestsService();

  constructor(
    private router: Router
  ) {}

  deepLoadPosts(customerId: number, context: PostLoadContext, page: number = 1, queryParams?: any): Promise<PostsData> {
    return new Promise((resolve, reject) => {
      this.postsService.getPosts(customerId, context, page, null, queryParams).then(paginationResult => {
        const posts = paginationResult.posts
        if (posts.length > 0) {
          const postIds = posts.map((p) => p.id)
          const writerIds = posts.map((p) => p.writerId)
          const keywordIds = posts.map((p) => p.keywordId)
          Promise.all<any>([this.writersService.getWriters(writerIds),
                            this.ratingsService.getRatings(customerId, postIds),
                            this.keywordsService.getKeywords(customerId, keywordIds)])
            .then((results: [Writer[], Rating[], Keyword[]]) => {
            const writers = results[0]
            const ratings = results[1]
            const keywords = results[2]
            resolve({
              posts: paginationResult.posts,
              totalPosts: paginationResult.meta.total,
              writers: writers,
              keywords: keywords,
              ratings: ratings
            })
          })
        } else {
          resolve({
            posts: [],
            totalPosts: 0,
            keywords: [],
            writers: [],
            ratings: []
          })
        }
      })
    })
  }

  loadPostsForAccount(context: PostLoadContext, page: number = 1, queryParams?: any): Promise<void> {
    const p = new Promise((resolve, reject) => {
      this.customersService.getCustomers().then(customers => {
        this.postsService.getPostsByAccount(context, page, null, queryParams).then((paginationResult) => {
          const groupedByCustomerId = groupBy(paginationResult.posts, 'customerId')
          Promise
          .all(Object.keys(groupedByCustomerId)
          .map(cid => this.getPostsData(parseInt(cid, 10), groupedByCustomerId[cid])))
          .then((customersPostsData: Array<any>) => {
            reactor.batch(() => {
              // Blank out each customers posts first
              customers.forEach(c => reactor.dispatch(customerOperations.setPostsForWriter, { customerId: c.id, writerId: queryParams.writerId, total: 0, posts: [] }) )
              customersPostsData.map((postsData: PostsData) => {
                const customerId = postsData.posts[0].customerId
                if (queryParams && queryParams.writerId) {
                  reactor.dispatch(customerOperations.setPostsForWriter, { customerId, writerId: queryParams.writerId, total: postsData.totalPosts, posts: postsData.posts })
                  reactor.dispatch(customerOperations.setAccountPostCounts, { total: paginationResult.meta.total, context: queryParams.writerId })
                } else {
                  reactor.dispatch(customerOperations.setPosts, { customerId, context, total: postsData.totalPosts, posts: postsData.posts })
                  reactor.dispatch(customerOperations.setAccountPostCounts, { total: paginationResult.meta.total, context: context })
                }
                reactor.dispatch(customerOperations.setWriters, postsData.writers)
                reactor.dispatch(customerOperations.updateKeywords, { customerId, keywords: postsData.keywords })
                reactor.dispatch(customerOperations.setRatings, { customerId, ratings: postsData.ratings })
              })
              reactor.dispatch(customerOperations.setCustomers, customers)
              resolve(null)
            })
          })
        })
      })
    })
    return donePromise(p)
  }

  private getPostsData(customerId: number, customerPosts: Post[]): Promise<PostsData> {
    return new Promise((resolve, reject) => {
      if (customerPosts.length > 0) {
        const postIds = customerPosts.map((p) => p.id)
        const writerIds = customerPosts.map((p) => p.writerId)
        const keywordIds = customerPosts.map((p) => p.keywordId)
        Promise.all<any>([
            this.writersService.getWriters(writerIds),
            this.ratingsService.getRatings(customerId, postIds),
            this.keywordsService.getKeywords(customerId, keywordIds)
          ]).then((results: [Writer[], PostReview[], Rating[], Keyword[]]) => {
          const writers = results[0]
          const postReviews = results[1]
          const ratings = results[2]
          const keywords = results[3]
          resolve({
            posts: customerPosts,
            writers: writers,
            ratings: ratings,
            keywords: keywords
          })
        })
      } else {
        resolve({
          posts: [],
          keywords: [],
          writers: [],
          ratings: []
        })
      }
    })
  }

  deepLoadPost(customerId: number, postId: number): Promise<PostData> {
    return new Promise((resolve, reject) => {
      this.postsService.getPost(customerId, postId).then((post) => {
        if (post) {
          Promise.all<any>([this.writersService.getWriter(post.writerId),
                            this.ratingsService.getRating(customerId, postId),
                            this.keywordsService.getKeyword(customerId, post.keywordId)])
            .then((results: [Writer, Rating, Keyword]) => {
            resolve({
              post: post,
              writer: results[0],
              rating: results[1],
              keyword: results[2]
            })
          })
        } else {
          resolve(null)
        }
      })
    })
  }

  // writer, postReview, rating, keyword
  loadPostsWithRelatedData(customerId: number, context: PostLoadContext, page: number = 1, queryParams?: any): Promise<any> {
    let p = this.deepLoadPosts(customerId, context, page, queryParams).then((postsData) => {
      reactor.batch(() => {
        if (queryParams && queryParams.writerId) {
          reactor.dispatch(customerOperations.setPostsForWriter, { customerId, writerId: queryParams.writerId, total: postsData.totalPosts, posts: postsData.posts })
        } else {
          reactor.dispatch(customerOperations.setPosts, { customerId, context, total: postsData.totalPosts, posts: postsData.posts })
        }
        reactor.dispatch(customerOperations.setWriters, postsData.writers)
        reactor.dispatch(customerOperations.updateKeywords, { customerId, keywords: postsData.keywords })
        reactor.dispatch(customerOperations.setRatings, { customerId, ratings: postsData.ratings })
      })
    })
    return donePromise(p)
  }

  loadPostWithRelatedData(customerId: number, postId: number): Promise<any> {
    let p = this.deepLoadPost(customerId, postId).then((postData) => {
      reactor.batch(() => {
        reactor.dispatch(customerOperations.setPost, {customerId, post: postData.post })
        reactor.dispatch(customerOperations.setWriter, postData.writer)
        reactor.dispatch(customerOperations.updateKeyword, { customerId, keywordId: postData.keyword.id, updates: postData.keyword })
        reactor.dispatch(customerOperations.updateRating, { customerId, postId: postData.post.id, updates: postData.post })
      })
    })
    return donePromise(p)
  }

  // eager for smoothness
  setRating(customerId: number, postId: number, newValue: number, oldValue: number, opts = {skipSuccessToast: false}): void {
    reactor.dispatch(customerOperations.updateRating, { customerId, postId, updates: { value: newValue } })
    this.ratingsService.updateRating(customerId, postId, newValue).then(() => {
      if (opts.skipSuccessToast) return
      successToast('Rating saved')
    }).catch(() => {
      errorToast('Rating failed to save')
      reactor.dispatch(customerOperations.updateRating, { customerId, postId, updates: { value: oldValue } })
    })
  }

  toggleExpanded(customerId: number, postId: number, expanded: boolean): void {
    const updates = { expanded }
    reactor.dispatch(customerOperations.updatePost, { customerId, postId, updates })
  }

  setFocusedPost(customerId: number, postId: number): void {
    reactor.dispatch(customerOperations.updateCustomer, { customerId, updates: { focusedPostId: postId } })
  }

  markViewed(customerId: number, postId: number): Promise<any> {
    const p = this.postsService.markViewed(customerId, postId).then(() => {
      reactor.dispatch(customerOperations.updatePost, { customerId, postId, updates: { state: 'viewed' } })
    })
    return donePromise(p)
  }

  togglePublished(customerId: number, postId: number, published: boolean): void {
    const stateString = published ? 'published' : 'not published'
    reactor.dispatch(customerOperations.updatePost, { customerId, postId, updates: { customerPublished: published } })
    this.postsService.setPublished(customerId, postId, published).then(() => {
      successToast('Marked as ' + stateString)
    }).catch(() => {
      reactor.dispatch(customerOperations.updatePost, { customerId, postId, updates: { customerPublished: !published } })
      errorToast('Failed to mark as ' + stateString)
    })
  }

  leaveReview(customerId: number, postId: number, comments: string, opts = {skipSuccessToast: false}): Promise<any> {
    const p = this.postsService.leaveReview(customerId, postId, comments).then(() => {
      this.deepLoadPost(customerId, postId).then(postData => {
        const post = postData.post
        post.appRating = postData.rating
        reactor.dispatch(customerOperations.setPost, { customerId, post })
        if (opts.skipSuccessToast) return
        successToast('Review saved')
      })
    })
    p.catch(() => {
      errorToast('Review failed to save')
    })
    return donePromise(p)
  }

  loadEditRequests(customerId: number, postId: number): Promise<any> {
    let p = this.postEditRequestsService.getPostEditRequests(customerId, postId)
    p.then(postEditRequests => {
      reactor.dispatch(customerOperations.setPostEditRequests, { customerId, postId, postEditRequests })
    })
    return p
  }

  updatePostEditRequests(customerId: number, postId: number, content: string, postEditRequests: PostEditRequest[]): Promise<PostEditRequest[]> {
    let p = this.postEditRequestsService.updatePostEditRequests(customerId, postId, content, postEditRequests)
    p.then(postEditRequests => {
      reactor.dispatch(customerOperations.setPostEditRequests, { customerId, postId, postEditRequests })
    }).catch(e => {})
    return p
  }

  takeBack(customerId: number, postId: number): Promise<any> {
    const p = this.postsService.takeBack(customerId, postId).then(() => {
      this.loadPostsWithRelatedData(customerId, 'queued').then(() => {
        successToast('Post added back to the end of the queue')
      })
    })
    return donePromise(p)
  }

  declinePost(customerId: number, postId: number, keywordKind: string, comments: any, reasons: number[], rating: number, updateUiAfter: Promise<any> = Promise.resolve()): Promise<any> {
    if (rating) this.setRating(customerId, postId, rating, 0, {skipSuccessToast: true})

    const p = Promise.all([this.postsService.declinePost(customerId, postId, comments, reasons), updateUiAfter]).then(() => {
      if (keywordKind === 'assigned') {
        successToast("You've declined this piece and we've removed the assigned writer from the request so other writers can meet your deadline.")
      } else {
        successToast('Post declined')
      }
      reactor.dispatch(customerOperations.updatePost, { customerId, postId, updates: { state: 'declined' } })
    })
    .catch(err => {
      errorToast('Failed to decline post')
    })
    return donePromise(p)
  }

  postNow(customerId: number, postId: number, paymentMethod: PurchasePostFormPaymentOptionType, extraCodes: PostExtraCode[] = [], appReview: string, rating: number, updateUiAfter: Promise<any> = Promise.resolve(), quantityFromCredits: number = null): Promise<any> {
    if (appReview) this.leaveReview(customerId, postId, appReview, {skipSuccessToast: true})
    if (rating) this.setRating(customerId, postId, rating, 0, {skipSuccessToast: true})
    const p = Promise.all([
      this.postsService.postNow(customerId, postId, paymentMethod, extraCodes, quantityFromCredits),
      this.customersService.getCustomer(customerId),
      updateUiAfter
    ]).then(([postNowResponse, customer]: [any, Customer, any]) => {
      reactor.batch(() => {
        reactor.dispatch(customerOperations.setCustomer, customer)
        reactor.dispatch(customerOperations.updatePost, { customerId, postId, updates: { state: 'posted' } })
      })
      successToast(postNowResponse.text || 'Purchase was successful')
    }).catch((res: { body: { error: string } }) => {
      if (paymentMethod === 'charge') {
        sweetAlert.openPaymentFailedModal().then(({value: clickedUpdatePaymentButton, dismiss}) => {
          if (clickedUpdatePaymentButton) {
            const returnUrl = `/dashboard/${customerId}/submissions/queued`
            this.router.navigate(['/dashboard', 'billing_info', 'edit'], { queryParams: { editing: true, returnUrl } })
          }
        })
      } else {
        errorToast(get(res, 'body.error', "An error occurred while purchasing. Please try again later."), { timeOut: 10000 })
      }
    })
    return donePromise(p)
  }

  pinPost(customerId: number, postId: number): Promise<any> {
    const p = this.postsService.pinPost(customerId, postId).then(() => {
      reactor.dispatch(customerOperations.updatePost, { customerId, postId, updates: { state: 'pinned' } })
      successToast('Post successfully pinned')
    })
    .catch(() => {
      errorToast('An error occurred while pinning post, please try again later.')
    })
    return donePromise(p)
  }

  unpinPost(customerId: number, postId: number): Promise<any> {
    const p = this.postsService.unpinPost(customerId, postId).then(() => {
      reactor.dispatch(customerOperations.updatePost, { customerId, postId, updates: { state: 'viewed' } })
      successToast('Post successfully unpinned')
    })
    .catch(() => {
      errorToast('An error occurred while unpinning post, please try again later.')
    })
    return donePromise(p)
  }

  refreshQueueAndCustomer(customerId: number): Promise<any> {
    const p = Promise.all([this.postsService.getPosts(customerId, 'queued'), this.customersService.getCustomer(customerId)]).then(([postsPage, customer]) => {
      reactor.batch(() => {
        reactor.dispatch(customerOperations.setCustomer, customer)
        reactor.dispatch(customerOperations.setPosts, { customerId, context: 'queued', total: postsPage.meta.total, posts: postsPage.posts })
      })
    })
    return p
  }

  sendToWriter(post: Post): Promise<any> {
    const p = this.postsService.sendToWriter(post).then(() => {
      reactor.dispatch(customerOperations.updatePost, { customerId: post.customerId, postId: post.id, updates: { state: 'awaiting_editing' } })
    })
    return p
  }

  toggleVisibility(customerId: number, postId: number, wlVisible: boolean): Promise<void> {
    const p = this.postsService.toggleVisibility(customerId, postId).then(res => {
      reactor.dispatch(customerOperations.updatePost, { customerId, postId, updates: { wlVisible } })
    })
    .catch(err => {
      errorToast('Failed to toggle approval')
    })
    return donePromise(p)
  }

  purchaseExtras(customerId: number, postId: number, extraCodes: PostExtraCode[], updateUiAfter: Promise<any> = Promise.resolve()): Promise<void> {
    const p = this.postsService.purchaseExtras(customerId, postId, extraCodes)
    Promise.all([p, updateUiAfter]).then(results => {
      const post = results[0]
      reactor.dispatch(customerOperations.updatePost, { customerId, postId, updates: post })
    }).catch(err => {
      errorToast('There was an error trying to purchase extras for this post')
    })
    return donePromise(p)
  }

}
